import React, { useState, useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { GuessResult } from '../../types/game';
import { cn } from '../../utils/cn';
import { DirectionBadge } from './DirectionBadge';
import { formatGuessMarketCap } from '../../utils/marketCapFormatting';
import { normalizeStockOption } from '../../utils/stockOptionFormatting';

interface VerticalGuessHistoryProps {
  guesses: GuessResult[];
  maxGuesses?: number;
  layout?: 'mobile-centered' | 'desktop-sidebar';
  className?: string;
  validationError?: string | null;
  onClearError?: () => void;
  isLoading?: boolean;
}

type Direction = 'higher' | 'lower' | 'correct' | 'unknown';

function getDirection(guess: GuessResult): Direction {
  if (guess.isCorrect) return 'correct';
  if (guess.marketCapDiff?.direction) return guess.marketCapDiff.direction;
  return 'unknown';
}

const NODE_COLORS: Record<Direction, string> = {
  higher: 'bg-blue-500',
  lower: 'bg-orange-500',
  correct: 'bg-green-500',
  unknown: 'bg-gray-400 dark:bg-gray-500'
};

/**
 * VerticalGuessHistory - Vertical timeline showing all guesses with direction badges.
 * Replaces the old horizontal carousel with a full vertical stack.
 */
export const VerticalGuessHistory: React.FC<VerticalGuessHistoryProps> = ({
  guesses,
  maxGuesses = 4,
  layout = 'mobile-centered',
  className,
  validationError,
  onClearError,
  isLoading = false
}) => {
  const [showError, setShowError] = useState(false);

  // Show/hide error with auto-dismiss
  useEffect(() => {
    if (validationError) {
      setShowError(true);
      const timer = setTimeout(() => {
        setShowError(false);
        onClearError?.();
      }, 4000);
      return () => clearTimeout(timer);
    } else {
      setShowError(false);
    }
  }, [validationError, onClearError]);

  const handleDismissError = () => {
    setShowError(false);
    onClearError?.();
  };

  const isDesktop = layout === 'desktop-sidebar';
  const hasCorrect = guesses.some(g => g.isCorrect);

  // Build slots: filled guesses + empty placeholders
  const slots: (GuessResult | null)[] = [];
  for (let i = 0; i < maxGuesses; i++) {
    slots.push(i < guesses.length ? guesses[i] : null);
  }

  return (
    <div className={cn('relative', className)}>
      {/* Floating Validation Error Banner */}
      {showError && validationError && (
        <div className={cn(
          'absolute -top-14 left-0 right-0 z-10',
          'animate-in fade-in slide-in-from-top-2 duration-300',
        )}>
          <div className={cn(
            'bg-amber-50 dark:bg-amber-950/90 border-2 border-amber-400 dark:border-amber-600',
            'rounded-lg shadow-lg',
            isDesktop ? 'p-3' : 'p-4'
          )}>
            <div className="flex items-center gap-2">
              <AlertTriangle className={cn(
                'flex-shrink-0 text-amber-600 dark:text-amber-400',
                isDesktop ? 'w-4 h-4' : 'w-5 h-5'
              )} />
              <p className={cn(
                'flex-1 text-amber-900 dark:text-amber-100 font-medium',
                isDesktop ? 'text-xs' : 'text-sm'
              )}>
                {validationError}
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDismissError}
                className={cn(
                  'p-1 h-auto min-w-0 hover:bg-amber-100 dark:hover:bg-amber-900',
                  'text-amber-600 dark:text-amber-400'
                )}
                aria-label="Dismiss error"
              >
                <X size={isDesktop ? 14 : 16} />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <span className={cn(
          'font-mono text-[10px] font-semibold uppercase tracking-widest text-muted-foreground',
        )}>
          Guess History
        </span>
        <span className={cn(
          'font-mono text-[10px] font-semibold',
          hasCorrect ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'
        )}>
          {guesses.length}/{maxGuesses}
        </span>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className={cn(
          'flex items-center justify-center gap-2 py-4 text-muted-foreground',
          isDesktop ? 'text-xs' : 'text-sm'
        )}>
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
          <span>Loading guess history...</span>
        </div>
      )}

      {/* Vertical timeline */}
      {!isLoading && (
        <div className="relative">
          {/* Timeline spine - only if there are filled guesses */}
          {guesses.length > 1 && (
            <div
              className="absolute left-[7px] top-[12px] w-[2px] bg-border dark:bg-gray-700 z-0"
              style={{ height: `calc(${guesses.length - 1} * ${isDesktop ? '60px' : '64px'})` }}
            />
          )}

          <div className={cn('space-y-3', isDesktop ? 'space-y-3' : 'space-y-3')}>
            {slots.map((guess, index) => {
              if (!guess) {
                // Empty placeholder slot
                return (
                  <div
                    key={`empty-${index}`}
                    className={cn(
                      'flex items-center gap-2',
                      isDesktop ? 'min-h-[48px]' : 'min-h-[52px]'
                    )}
                  >
                    {/* Timeline node - empty */}
                    <div className={cn(
                      'relative z-10 flex-shrink-0 w-4 h-4 rounded-full border-2 border-dashed',
                      'border-gray-400 dark:border-gray-500'
                    )} />

                    {/* Empty card */}
                    <div className={cn(
                      'flex-1 rounded-lg border-2 border-dashed',
                      'border-gray-400 dark:border-gray-600',
                      isDesktop ? 'px-3 py-2' : 'px-3 py-2.5'
                    )}>
                      <span className={cn(
                        'text-gray-400 dark:text-gray-500 font-mono',
                        isDesktop ? 'text-[10px]' : 'text-xs'
                      )}>
                        Guess {index + 1}
                      </span>
                    </div>
                  </div>
                );
              }

              const direction = getDirection(guess);
              const isSkipped = guess.isSkipped;

              // Extract ticker from normalized display (e.g. "$AAPL: Apple Inc." -> "AAPL")
              const parsed = normalizeStockOption(guess.company);
              const ticker = parsed.ticker || '';
              const companyName = parsed.company || guess.company;

              return (
                <div
                  key={`guess-${index}`}
                  className={cn(
                    'flex items-center gap-2',
                    isDesktop ? 'min-h-[48px]' : 'min-h-[52px]',
                    // Staggered animation
                    'animate-in fade-in slide-in-from-bottom-2 duration-300',
                  )}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {/* Timeline node - filled */}
                  <div className={cn(
                    'relative z-10 flex-shrink-0 w-4 h-4 rounded-full',
                    NODE_COLORS[direction]
                  )} />

                  {/* Guess card */}
                  <div className={cn(
                    'flex-1 min-w-0 rounded-lg border flex items-center justify-between gap-2',
                    'bg-card',
                    guess.isCorrect
                      ? 'border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-950/20'
                      : 'border-border',
                    isDesktop ? 'px-3 py-1.5' : 'px-3 py-2'
                  )}>
                    {/* Left side: ticker + name + market cap */}
                    <div className="flex-1 min-w-0">
                      {isSkipped ? (
                        <span className={cn(
                          'italic text-gray-400 dark:text-gray-500',
                          isDesktop ? 'text-xs' : 'text-sm'
                        )}>
                          Skipped
                        </span>
                      ) : (
                        <div className="flex items-center gap-2 min-w-0">
                          {/* Ticker badge */}
                          {ticker && (
                            <span className={cn(
                              'flex-shrink-0 font-mono font-bold inline-flex items-center justify-center rounded-md',
                              'bg-gray-100 dark:bg-gray-800',
                              isDesktop ? 'text-xs px-2 py-1' : 'text-sm px-2.5 py-1.5',
                              guess.isCorrect
                                ? 'text-green-700 dark:text-green-300'
                                : 'text-foreground'
                            )}>
                              ${ticker}
                            </span>
                          )}
                          {/* Company name + market cap */}
                          <div className="flex flex-col min-w-0">
                            <span className={cn(
                              'truncate whitespace-nowrap overflow-hidden',
                              isDesktop ? 'text-[11px]' : 'text-xs',
                              'text-muted-foreground'
                            )}>
                              {ticker ? companyName : guess.company}
                            </span>
                            {guess.guessedMarketCap && guess.guessedMarketCap > 0 ? (
                              <span className={cn(
                                'font-mono text-[#94a3b8]',
                                isDesktop ? 'text-[11px]' : 'text-xs'
                              )}>
                                {formatGuessMarketCap(guess.guessedMarketCap)}
                              </span>
                            ) : !guess.isCorrect && !isSkipped ? (
                              <span className={cn(
                                'italic text-muted-foreground/40',
                                isDesktop ? 'text-[11px]' : 'text-xs'
                              )}>
                                Market cap unavailable
                              </span>
                            ) : null}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right side: direction badge */}
                    {!isSkipped && (
                      <DirectionBadge direction={direction} />
                    )}
                    {isSkipped && (
                      <DirectionBadge direction="unknown" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
